import React, {FC} from 'react';
import classNames from 'classnames';
import MobileWrapper from 'components/Header/MobileWrapper';
import ReloadLink from 'components/Header/ReloadLink';
import {Book} from 'components/Header/TopNav';
import {Routes} from 'constants/index';
import {navigate} from 'gatsby';
import {usePermissions} from 'hooks/usePermissions';
import {hasWindow} from 'utils/functions';
import {useRedirectURL} from 'utils/redirects';

import {NavProps} from '..';

import User from './svgs/user.inline.svg';
import DesktopNav from './DesktopNav';
import MobileNav from './MobileNav';

import * as styles from './styles.module.scss';

type AccountButtonProps = {
	desktopOnly?: boolean
	name?: string
	onClick?: () => void
	children?: React.ReactNode
}

const AccountButton: FC<AccountButtonProps> = (props) => {
	const {auth} = usePermissions();

	const handleClick = () => {
		// If an onClick prop is provided, call it
		if (props.onClick) {
			props.onClick();
		}
		// If the user is authenticated and this is a desktop-only button, redirect to the dashboard
		else if (auth.isValidUser() && props.desktopOnly) {
			navigate(Routes.dashboard);
		}
	};

	return (
		<div className={classNames({
			[styles.wrapper]: true,
			[styles.desktop]: props.desktopOnly,
		})}>
			<div className={styles.clickable} onClick={handleClick}>
				<span className={styles.named}>
					{props.name || 'Sign in'}
				</span>
				<span>{'>'}</span>
				<User />
			</div>
			{props.children}
		</div>
	);
};

const SignIn: FC<Pick<AccountButtonProps, 'desktopOnly'>> = (props) => {
	// Check if we're on the reset-success page to prevent circular navigation
	const isResetSuccessPage = hasWindow() && window.location.pathname === Routes.resetSuccess;

	// If on reset-success page, use the sign-in URL without redirect parameter
	// Otherwise, use the normal redirect URL behavior
	const url = isResetSuccessPage ? Routes.signIn : useRedirectURL(Routes.signIn);

	return (
		<ReloadLink to={url} className={classNames({
			[styles.signIn]: true,
			[styles.desktop]: props.desktopOnly,
		})}>
			<AccountButton />
		</ReloadLink>
	);
};

const AccountNav: FC<NavProps> = ({nav}) => {
	const {auth} = usePermissions();

	// Logged in, display name & navs
	if (auth.isValidUser() && auth.user) {
		// We don't want to engage a static overlay when clicking the
		// desktop version of the sign-in. So we keep around two divs.
		// The mobile version responds to clicks and desktop version does
		// not. Only one is ever shown based on media queries.
		return (
			<>
				<div className={styles.mobileWrapper}>
					<AccountButton name={auth.user.name} onClick={nav.open}>
						{nav.active && (
							<MobileWrapper side='right' onClose={nav.close}>
								<MobileNav
									name={auth.user.name}
									nav={nav}
								/>
							</MobileWrapper>
						)}
					</AccountButton>
					<Book className={styles.mobileBook} />
				</div>

				<AccountButton desktopOnly name={auth.user.name}>
					<DesktopNav
						className={styles.desktopNav}
						name={auth.user.name}
						nav={nav}
					/>
				</AccountButton>
			</>
		);
	}

	// Not logged in, display simpler version of buttons
	return (
		<>
			<div className={styles.mobileWrapper}>
				<SignIn />
				<Book className={styles.mobileBook} />
			</div>

			<SignIn desktopOnly />
		</>
	);
};

export default AccountNav;
